import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CircleDot, HardHat, CalendarClock, XCircle, Columns3, List, SlidersHorizontal } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import PripadForm from "../components/PripadForm";
import Napoveda from "../components/Napoveda";
import Segment from "../components/Segment";
import FiltrLista, { useFiltr, SkrytoFiltrem, PrazdnyFiltr } from "../components/FiltrLista";
import { Tabule, TabuleMobil, KartaMenu, tabuleEtapy, chladne, uzavreny } from "../components/ObchodTabule";
import ObchodSeznam from "../components/ObchodSeznam";
import PripadNahled from "../components/PripadNahled";
import { useZobrazeni, uzavreneOd, ZobrazeniPanel, TahakZkratek } from "../components/ObchodZobrazeni";
import AktivitaDetail from "../components/AktivitaDetail";
import NovaAktivita from "../components/NovaAktivita";
import { useTym } from "../components/KdoUvidi";
import { DUVODY } from "../components/TechKroky";
import { Button, Spinner } from "../components/ui/kit";
import { etapa, fazeBarva, nazevEtapy } from "../lib/faze";
import { fmtKcKratce } from "../lib/format";
import { odpovida, sklon } from "../lib/filtr";
import { poleTechnologie, poleClovek, poleKlient, polePartner, poleHodnota, poleZmena, polePevne } from "../lib/filtryPole";
import { Kbd, useZkratky } from "../lib/zkratky";
import { useJeMobil } from "../lib/mobil";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import api from "../lib/api";
import { useUrlStav } from "../lib/navigace";

const TVARY = ["případ", "případy", "případů"];
// Řazení na klientovi (načtené jsou všechny případy do 500).
const KLIC_RAZENI = {
  zmena: (p) => p.updated_at || p.created_at || "",
  hodnota: (p) => p.hodnota || 0,
  krok: (p) => p.dalsi_krok?.date_start || "9999",
  dni: (p) => p.dni_ve_fazi || 0,
  nazev: (p) => (p.nazev || "").toLowerCase(),
};

export default function Obchod() {
  const nav = useNavigate();
  const { user } = useAuth();
  const tym = useTym();
  const mobil = useJeMobil();
  const canWrite = canSee(user, ["OWNER", "OBCHODNIK"]);
  const isOwner = canSee(user, ["OWNER"]);
  const [data, setData] = useState(null);
  const [q, setQ] = useUrlStav("q", "");
  const [pohled, setPohled] = useUrlStav("pohled", "vse");
  const [fazeMobil, setFazeMobil] = useUrlStav("faze");
  const [z, nastavZ, resetZ] = useZobrazeni(user?.id);
  const [form, setForm] = useState(false);
  const [clients, setClients] = useState([]);
  const [menu, setMenu] = useState(null);            // { p, kotva }
  const [nahled, setNahled] = useState(null);        // případ
  const [aktivita, setAktivita] = useState(null);    // id aktivity
  const [planovat, setPlanovat] = useState(null);    // případ
  const [upravit, setUpravit] = useState(null);      // aktivita k úpravě
  const [zobrazeni, setZobrazeni] = useState(false);
  const [tahak, setTahak] = useState(false);
  const zobrazeniBtn = useRef(null);

  const od = uzavreneOd(z);
  const load = useCallback(async () => {
    const { data } = await api.get("/api/obchodni-pripady", { params: { q, limit: 500, karty: true, uzavrene_od: od || undefined } });
    setData(data);
  }, [q, od]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (form) api.get("/api/clients?limit=200").then((r) => setClients(r.data.items || [])); }, [form]);

  const money = !!data?.muze_ceny;
  const tymMapa = useMemo(() => Object.fromEntries(tym.map((m) => [m.id, m])), [tym]);
  const pole = useMemo(() => [
    polePevne({ klic: "faze", nazev: "Fáze", rod: "z", ikona: CircleDot, tvary: ["fáze", "fáze", "fází"],
      // Mezikroky firmy jsou vlastní hodnoty; „Nabídka“ najde i případy v mezikrocích.
      hodnoty: [...tabuleEtapy(), "ZTRACENO"].map((f) => ({ v: f, label: nazevEtapy(f), tecka: fazeBarva(f).dot })),
      hodnotyPolozky: (p) => [...new Set([p.faze, etapa(p)])] }),
    poleTechnologie((p) => (p.technologie || []).filter((t) => t.faze !== "ZTRACENO").map((t) => t.kategorie_name)),
    poleClovek({ klic: "obchodnik", nazev: "Obchodník", tym, me: user?.id, id: (p) => p.obchodnik_id }),
    poleClovek({ klic: "vedouci", nazev: "Vedoucí montáže", ikona: HardHat, tym, me: user?.id, id: (p) => p.vedouci_montaze_id }),
    poleKlient((p) => p.client_id, (p) => p.client_name),
    ...(data?.items?.some((p) => "partner" in p) ? [polePartner((p) => p.partner)] : []),
    polePevne({ klic: "krok", nazev: "Další krok", rod: "m", ikona: CalendarClock, tvary: ["stav", "stavy", "stavů"],
      hodnoty: [{ v: "NAPLANOVANY", label: "Naplánovaný" }, { v: "CHYBI", label: "Chybí" }, { v: "PO_TERMINU", label: "Po termínu", tecka: "#d93a2b" }],
      hodnotyPolozky: (p) => (uzavreny(p) ? [] : [!p.dalsi_krok ? "CHYBI" : p.dalsi_krok.po_terminu ? "PO_TERMINU" : "NAPLANOVANY"]) }),
    ...(money ? [poleHodnota((p) => p.hodnota)] : []),
    polePevne({ klic: "duvod", nazev: "Důvod ztráty", rod: "m", ikona: XCircle, tvary: ["důvod", "důvody", "důvodů"],
      hodnoty: Object.entries(DUVODY).map(([v, label]) => ({ v, label })), hodnotyPolozky: (p) => (p.duvod_ztraty ? [p.duvod_ztraty] : []) }),
    poleZmena((p) => p.updated_at || p.created_at),
  ], [tym, user?.id, money, data]);
  const [filtry, setFiltry] = useFiltr(pole);

  // Ztracené jen když je Zobrazení ukazuje; jinak by mátly součty.
  const items = useMemo(() => (data?.items || []).filter((p) => z.ztracene || p.faze !== "ZTRACENO"), [data, z.ztracene]);
  const pohledy = useMemo(() => [
    { klic: "vse", nazev: "Všechny", test: () => true },
    { klic: "moje", nazev: "Moje", test: (p) => p.moje },
    { klic: "chladnou", nazev: "Váznou", test: chladne,
      popis: "Bez dalšího kroku, krok po termínu, nebo déle než 14 dní ve stejné fázi" },
  ].map((p) => ({ ...p, pocet: items.filter(p.test).length })), [items]);
  const rozsah = useMemo(() => items.filter((pohledy.find((p) => p.klic === pohled) || pohledy[0]).test), [items, pohledy, pohled]);
  const shown = useMemo(() => {
    const k = KLIC_RAZENI[z.razeni] || KLIC_RAZENI.zmena;
    const smer = z.smer === "asc" ? 1 : -1;
    return rozsah.filter((p) => odpovida(p, filtry, pole))
      .sort((a, b) => (k(a) < k(b) ? -1 : k(a) > k(b) ? 1 : 0) * smer);
  }, [rozsah, filtry, pole, z.razeni, z.smer]);

  // Souhrn v hlavičce: otevřené případy po filtru.
  const otevrene = shown.filter((p) => !uzavreny(p));
  const suma = otevrene.reduce((a, p) => a + (p.hodnota || 0), 0);
  const poTerminu = otevrene.filter((p) => p.dalsi_krok?.po_terminu).length;
  const chladnych = otevrene.filter(chladne).length;
  const zapniFiltr = (pole_, hodnota) => setFiltry((fs) => [...fs.filter((f) => f.pole !== pole_), { pole: pole_, neg: false, hodnoty: [hodnota] }]);

  // Telefon: fáze z adresy, pak naposledy vybraná, jinak ta s nejvíc případy.
  const fazeTel = useMemo(() => {
    if (fazeMobil) return fazeMobil;
    try { const ulozena = window.sessionStorage.getItem("pp-obchod-faze"); if (ulozena) return ulozena; } catch { /* nic */ }
    const pocet = (f) => shown.filter((p) => etapa(p) === f).length;
    return tabuleEtapy().reduce((nej, f) => (pocet(f) > pocet(nej) ? f : nej), "NOVY");
  }, [fazeMobil, shown]);
  const vyberFaziTel = (f) => { setFazeMobil(f); try { window.sessionStorage.setItem("pp-obchod-faze", f); } catch { /* nic */ } };

  useZkratky({
    c: () => canWrite && setForm(true),
    v: () => setZobrazeni((x) => !x),
    1: () => nastavZ({ rozvrzeni: "seznam" }),
    2: () => nastavZ({ rozvrzeni: "tabule" }),
    "?": () => setTahak(true),
  });

  const akce = {
    money, tymMapa,
    onDetail: (p) => nav(`/obchod/${p.id}`),
    onMenu: (p, kotva) => setMenu({ p, kotva }),
    onNahled: (p) => setNahled(p),
    onKrok: (id) => setAktivita(id),
  };
  const tabule = z.rozvrzeni === "tabule";
  const prepinac = (
    <Segment className="filtr-pas" data-testid="obchod-rozvrzeni">
      {[["seznam", "Seznam", List], ["tabule", "Tabule", Columns3]].map(([v, label, Ikona]) => (
        <button key={v} type="button" data-on={z.rozvrzeni === v} onClick={() => nastavZ({ rozvrzeni: v })} data-testid={`rozvrzeni-${v}`}
          aria-label={label} className={`filtr-seg gap-1.5 ${z.rozvrzeni === v ? "text-white" : "text-sec hover:text-ink"}`}>
          <Ikona size={14} /><span className="hidden md:inline">{label}</span>
        </button>
      ))}
    </Segment>
  );
  const tlacitkoZobrazeni = (
    <button ref={zobrazeniBtn} type="button" onClick={() => setZobrazeni((x) => !x)} aria-expanded={zobrazeni} data-testid="obchod-zobrazeni"
      aria-label="Zobrazení" className="filtr bg-white text-ink border-line-strong hover:bg-inputbg">
      <SlidersHorizontal size={15} /><span className="hidden md:inline">Zobrazení</span><Kbd>V</Kbd>
    </button>
  );

  return (
    <div data-testid="obchod-page">
      <PageHeader eyebrow="Obchod"
        title={<>Obchodní případy{!mobil && <>{" "}
          <Napoveda className="align-middle" testid="obchod-napoveda">Fáze případu je nejpomalejší technologie. Další krok spustíš přes ⋯ na kartě — co chybí, doplníš v panelu.</Napoveda></>}</>}
        subtitle={data && (
          <span data-testid="obchod-souhrn">
            {otevrene.length} {sklon(otevrene.length, TVARY)}{money ? ` · ${fmtKcKratce(suma)}` : ""}
            {poTerminu > 0 && <> · <button type="button" onClick={() => zapniFiltr("krok", "PO_TERMINU")} className="text-danger hover:underline">{poTerminu} po termínu</button></>}
            {chladnych > 0 && <> · <button type="button" onClick={() => setPohled("chladnou")} className="text-[#b7791f] hover:underline"
              title="Bez dalšího kroku, krok po termínu, nebo déle než 14 dní ve stejné fázi">{chladnych} {sklon(chladnych, ["vázne", "váznou", "vázne"])}</button></>}
          </span>
        )}
        actions={canWrite && (
          <Button onClick={() => setForm(true)} data-testid="new-pripad"><Plus size={16} /> Nový případ <Kbd tmavy>C</Kbd></Button>
        )} />

      <FiltrLista pole={pole} polozky={rozsah} filtry={filtry} setFiltry={setFiltry} tvary={TVARY}
        pohledy={pohledy} pohled={pohled} setPohled={setPohled} vysledek={shown.length} celkem={rozsah.length}
        vlevo={!mobil && prepinac} vpravo={tlacitkoZobrazeni}
        hledani={{ q, setQ, placeholder: "Hledat klienta, číslo, technologii…", testid: "obchod-search" }} testid="obchod-filtr" />
      {data && data.total > (data.items || []).length && (
        <div className="mb-3 t-meta text-muted" data-testid="obchod-orez">Zobrazeno {data.items.length} z {data.total} — upřesni hledání nebo filtr.</div>
      )}

      {!data ? (
        <div className="card p-10 flex justify-center text-muted"><Spinner /></div>
      ) : items.length === 0 ? (
        <div className="card"><EmptyState icon="Briefcase" title={q ? "Nic nenalezeno" : "Zatím žádné případy"}
          description={q ? "Zkus jiné hledání." : "Založ první obchodní případ ke klientovi."}
          action={canWrite && !q && <Button onClick={() => setForm(true)}><Plus size={16} /> Nový případ</Button>} /></div>
      ) : shown.length === 0 ? (
        <div className="card">{filtry.length ? <PrazdnyFiltr nazev="Žádný případ" onVymazat={() => setFiltry([])} />
          : <EmptyState icon="Briefcase" title="V tomhle pohledu nic není" description="Přepni na Všechny." />}</div>
      ) : tabule ? (
        mobil
          ? <TabuleMobil polozky={shown} ztracene={z.ztracene} vl={z.vlastnosti} faze={fazeTel} setFaze={vyberFaziTel} {...akce} />
          : <Tabule polozky={shown} ztracene={z.ztracene} vl={z.vlastnosti} canWrite={canWrite} onNovy={() => setForm(true)} {...akce} />
      ) : (
        <ObchodSeznam polozky={shown} z={z} me={user?.id} mobil={mobil} {...akce}
          setRazeni={(k) => nastavZ((s) => (s.razeni === k ? { smer: s.smer === "asc" ? "desc" : "asc" } : { razeni: k, smer: k === "nazev" ? "asc" : "desc" }))} />
      )}
      {data && shown.length > 0 && filtry.length > 0 && (
        <SkrytoFiltrem skryto={rozsah.length - shown.length} tvary={TVARY} onZobrazit={() => setFiltry([])} />
      )}

      {menu && <KartaMenu p={menu.p} kotva={menu.kotva} onClose={() => setMenu(null)} smiPlanovat={canWrite}
        onNahled={(p) => setNahled(p)} onNaplanovat={(p) => setPlanovat(p)} onDetail={akce.onDetail} />}
      {nahled && <PripadNahled polozka={nahled} money={money} isOwner={isOwner} smiPlanovat={canWrite}
        onClose={() => setNahled(null)} onDetail={akce.onDetail} onNaplanovat={(p) => setPlanovat(p)} onKrok={akce.onKrok} onZmena={load} />}
      {aktivita && <AktivitaDetail id={aktivita} onClose={() => setAktivita(null)} onChanged={load} onEdit={(a) => { setAktivita(null); setUpravit(a); }} />}
      {upravit && <NovaAktivita aktivita={upravit} onClose={() => setUpravit(null)} onSaved={() => { setUpravit(null); load(); }} />}
      {planovat && <NovaAktivita defaultClientId={planovat.client_id} pripadId={planovat.id}
        onClose={() => setPlanovat(null)} onSaved={() => { setPlanovat(null); load(); }} />}
      {zobrazeni && <ZobrazeniPanel z={z} nastav={nastavZ} reset={resetZ} money={money} kotva={zobrazeniBtn.current}
        onClose={() => setZobrazeni(false)} />}
      {tahak && <TahakZkratek onClose={() => setTahak(false)} />}
      {form && <PripadForm clients={clients} onClose={() => setForm(false)} onSaved={(d) => nav(`/obchod/${d.id}`)} />}
    </div>
  );
}
